/**
 * Appearance preferences, applied to the surfaces they name.
 *
 * The module exists because the alternative is worse than untidy: a setting
 * with no consumer still renders a row, still persists, still shows a reset
 * button, and does nothing. That is indistinguishable from a working setting
 * until someone measures it, and we already shipped one — "Code font size"
 * claims code, diffs and the terminal, while `themeSurfaceGuard` actively
 * asserts chat code blocks must not read its variable.
 *
 * So every preference here is written as a custom property with a *named*
 * consumer, and `appearanceWiringGuard.browser.tsx` asserts each one changes a
 * computed style on the surface it claims. A preference that reaches nothing
 * fails that guard rather than shipping quietly.
 *
 * Sizes are absolute px on purpose. The interface size already drives the root
 * font size, so a rem-based prompt or code size would scale twice: pick a 20px
 * interface and a "14px" code block renders at 17.5.
 *
 * The contrast preference is the one delegation: upstream's
 * `applyAppearanceContrast` owns its three knobs, and the `--contrast-*` axis
 * in `index.css` derives the roles from them, so the variables live in the
 * map below only so the guard and the cleanup can see them.
 *
 * @module
 */
import type { AppearanceContrast } from "@cmmd-forge/contracts/settings";

import { applyAppearanceContrast } from "./appearanceContrast";

/**
 * The shipped stacks, duplicated from `index.css` because a custom family is
 * *prepended* to them rather than replacing them — a display face chosen for
 * Latin must not silently cost coverage for every script it lacks.
 */
const DEFAULT_SANS_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

/**
 * Concrete names before the generic. Some engines alias `ui-monospace` to the
 * proportional system UI font, which would silently break every code surface
 * and the terminal grid with it.
 */
const DEFAULT_MONO_FONT_STACK =
  'ui-monospace, "SF Mono", "SFMono-Regular", Menlo, Consolas, "Liberation Mono", monospace';

export interface AppearanceSurfacePreferences {
  readonly appearanceContrast: AppearanceContrast;
  readonly fontFamilySans: string;
  readonly fontFamilyCode: string;
  readonly fontFamilyComposer: string;
  readonly fontFamilyTerminal: string;
  readonly promptFontSize: number;
  readonly terminalFontSize: number;
  readonly fontSmoothing: boolean;
  readonly glassOpacity: number;
}

/** Quote a family name only when CSS requires it. */
function quoteFamily(name: string): string {
  const bare = name.trim();
  if (bare.length === 0) return "";
  if (/^(['"]).*\1$/u.test(bare)) return bare;
  if (/^[a-zA-Z][a-zA-Z0-9-]*$/u.test(bare)) return bare;
  return `"${bare.replaceAll('"', "")}"`;
}

/**
 * A user-entered family (one name, or a comma-separated list) as a safe CSS
 * font-family list, or null when the input is effectively empty.
 */
function cssFontFamilies(input: string): string | null {
  const families = input
    .split(",")
    .map(quoteFamily)
    .filter((name) => name.length > 0);
  return families.length > 0 ? families.join(", ") : null;
}

/**
 * Every custom property this module owns, paired with the surface that reads
 * it. Kept as data rather than a sequence of `setProperty` calls so the guard
 * test can enumerate it and fail on a property nobody consumes.
 */
export const APPEARANCE_SURFACE_VARIABLES = {
  appearanceContrastBase: "--appearance-contrast-base",
  appearanceContrastBoost: "--appearance-contrast-boost",
  appearanceContrastBorderBoost: "--appearance-contrast-border-boost",
  fontSans: "--font-sans",
  fontMono: "--font-mono",
  fontComposer: "--font-composer",
  fontTerminal: "--font-terminal",
  promptFontSize: "--forge-prompt-font-size",
  terminalFontSize: "--forge-terminal-font-size",
  glassOpacity: "--glass-opacity",
} as const;

/**
 * Writes the preferences onto `root` (always `documentElement` in the app).
 *
 * Families that resolve to nothing *remove* the override rather than writing
 * an empty string, so the stylesheet default stays in charge and a later theme
 * change is not shadowed by a dead inline value.
 */
export function applyAppearanceSurfaces(
  root: HTMLElement,
  preferences: AppearanceSurfacePreferences,
): void {
  const families: ReadonlyArray<readonly [variable: string, custom: string, fallback: string]> = [
    [APPEARANCE_SURFACE_VARIABLES.fontSans, preferences.fontFamilySans, DEFAULT_SANS_FONT_STACK],
    [APPEARANCE_SURFACE_VARIABLES.fontMono, preferences.fontFamilyCode, DEFAULT_MONO_FONT_STACK],
    // The composer falls back to whatever the sans preference resolved to, so
    // a user who only sets an interface font still gets it in the composer.
    [APPEARANCE_SURFACE_VARIABLES.fontComposer, preferences.fontFamilyComposer, "var(--font-sans)"],
    // The terminal falls back to the code font for the same reason.
    [APPEARANCE_SURFACE_VARIABLES.fontTerminal, preferences.fontFamilyTerminal, "var(--font-mono)"],
  ];

  for (const [variable, custom, fallback] of families) {
    const list = cssFontFamilies(custom);
    if (list === null) {
      root.style.removeProperty(variable);
      continue;
    }
    root.style.setProperty(variable, `${list}, ${fallback}`);
  }

  root.style.setProperty(
    APPEARANCE_SURFACE_VARIABLES.promptFontSize,
    `${String(preferences.promptFontSize)}px`,
  );
  root.style.setProperty(
    APPEARANCE_SURFACE_VARIABLES.terminalFontSize,
    `${String(preferences.terminalFontSize)}px`,
  );
  root.style.setProperty(
    APPEARANCE_SURFACE_VARIABLES.glassOpacity,
    `${String(preferences.glassOpacity)}%`,
  );

  applyAppearanceContrast(root, preferences.appearanceContrast);

  // Inherited from the root, and only Apple engines honour it, so no platform
  // gate is needed here — elsewhere it is inert rather than wrong. On means
  // grayscale `antialiased` (thinner stems); off restores the platform
  // default, which macOS renders with heavier stem darkening.
  if (preferences.fontSmoothing) {
    root.style.setProperty("-webkit-font-smoothing", "antialiased");
  } else {
    root.style.removeProperty("-webkit-font-smoothing");
  }
}
